"use client"

import { useEffect } from "react"

import {
  RECLAME_AQUI_COMPANY_URL,
  RECLAME_AQUI_SEAL_MODEL,
  RECLAME_AQUI_VERIFIED_BUNDLE_URL,
  RECLAME_AQUI_VERIFIED_HASH,
} from "@/lib/reclame-aqui"

type PendingSeal = {
  targetId: string
  model: string
}

const queue: PendingSeal[] = []
let flushScheduled = false

const VERIFIED_MARK_URL =
  "https://s3.amazonaws.com/raichu-beta/ra-verified/assets/images/verified.svg"
const RA_LOGO_URL = "https://s3.amazonaws.com/raichu-beta/ra-verified/assets/images/ra-logo.svg"

function sealLinksToAstra(host: HTMLElement): boolean {
  const link = host.querySelector("a")
  const href = link?.getAttribute("href") ?? ""
  return href.includes("/empresa/astra-energia")
}

/** Se a API do embed não responder, o bundle oficial cai no selo genérico da home do RA. */
function applyAstraVerifiedFallback(host: HTMLElement) {
  if (sealLinksToAstra(host)) return
  host.replaceChildren()
  const link = document.createElement("a")
  link.href = RECLAME_AQUI_COMPANY_URL
  link.target = "_blank"
  link.rel = "noopener noreferrer"
  link.title = "Selo RA Verificada"
  link.className =
    "flex h-[52px] w-[142px] items-center justify-center gap-1.5 rounded-lg border border-[#1e40af] bg-white px-1.5"
  const mark = document.createElement("img")
  mark.src = VERIFIED_MARK_URL
  mark.alt = ""
  mark.width = 28
  mark.height = 28
  const copy = document.createElement("span")
  copy.className = "flex flex-col items-start leading-none"
  const label = document.createElement("span")
  label.className = "text-[9px] font-medium text-[#0f172a]"
  label.textContent = "Verificada por"
  const logo = document.createElement("img")
  logo.src = RA_LOGO_URL
  logo.alt = "Reclame AQUI"
  logo.width = 74
  logo.height = 14
  copy.append(label, logo)
  link.append(mark, copy)
  host.append(link)
}

function watchAstraLink(targetId: string) {
  const host = document.getElementById(targetId)
  if (!host) return
  const apply = () => {
    if (sealLinksToAstra(host)) return
    if (host.querySelector("a")) applyAstraVerifiedFallback(host)
  }
  const observer = new MutationObserver(apply)
  observer.observe(host, { childList: true, subtree: true })
  window.setTimeout(apply, 4000)
}

function flushSeals() {
  const batch = queue.splice(0, queue.length)
  flushScheduled = false
  if (batch.length === 0) return

  for (const [index, item] of batch.entries()) {
    const script = document.createElement("script")
    script.id = "ra-embed-verified-seal"
    script.async = true
    script.setAttribute("data-id", RECLAME_AQUI_VERIFIED_HASH)
    script.setAttribute("data-target", item.targetId)
    script.setAttribute("data-model", item.model)
    if (index === 0) script.src = RECLAME_AQUI_VERIFIED_BUNDLE_URL
    document.body.appendChild(script)
    watchAstraLink(item.targetId)
  }
}

function scheduleSeal(item: PendingSeal) {
  queue.push(item)
  if (flushScheduled) return
  flushScheduled = true
  const run = () => flushSeals()
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run, { timeout: 2000 })
  } else {
    window.setTimeout(run, 1)
  }
}

type ReclameAquiVerifiedSealProps = {
  targetId: string
  model?: string
}

/**
 * Embed oficial do selo RA Verificada. O script entra no idle.
 * Não há CSP no repositório; o bundle precisa de
 * s3.amazonaws.com, api.reclameaqui.com.br e das fontes que o CSS do selo importa.
 */
export function ReclameAquiVerifiedSeal({
  targetId,
  model = RECLAME_AQUI_SEAL_MODEL,
}: ReclameAquiVerifiedSealProps) {
  useEffect(() => {
    const host = document.getElementById(targetId)
    if (!host || host.dataset.raSealMounted === "1") return
    host.dataset.raSealMounted = "1"
    const item = { targetId, model }
    scheduleSeal(item)
    return () => {
      const index = queue.indexOf(item)
      if (index >= 0) {
        queue.splice(index, 1)
        delete host.dataset.raSealMounted
      }
    }
  }, [model, targetId])

  return (
    <div className="inline-flex h-[52px] w-[142px] shrink-0 items-center justify-center">
      <div id={targetId} />
    </div>
  )
}
